/**
 * Database Service
 * 
 * This service provides a clean abstraction layer for all database operations.
 * All Firestore-specific code is encapsulated here.
 * 
 * Migration Strategy:
 * - When migrating to Azure Cosmos DB, PostgreSQL, or another database:
 *   1. Keep the same function signatures (inputs/outputs)
 *   2. Replace Firestore calls with new database equivalents
 *   3. Maintain the same data structure where possible
 *   4. Components using this service won't need changes
 * 
 * Example Azure Cosmos DB Migration:
 * - Replace collection() with container references
 * - Replace onSnapshot with change feed
 * - Replace addDoc with container.items.create
 * - Map Firestore queries to SQL or NoSQL equivalents
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ==================== USER OPERATIONS ====================

/**
 * Create or update user profile in database
 * @param {string} uid - User ID
 * @param {Object} userData - User data
 * @returns {Promise<void>}
 */
export const createOrUpdateUser = async (uid, userData) => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error creating/updating user:', error);
    throw new Error('Failed to save user data');
  }
};

/**
 * Get user profile by UID
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>} User data or null
 */
export const getUserById = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw new Error('Failed to fetch user data');
  }
};

/**
 * Search users by verified tag
 * @param {string} searchTag - Tag to search for (e.g., @ChannelName#YT)
 * @returns {Promise<Array>} Array of matching users
 */
export const searchUsersByTag = async (searchTag) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('verifiedTag', '==', searchTag),
      limit(10)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error searching users:', error);
    throw new Error('Failed to search users');
  }
};

/**
 * Generate a unique verified tag for a channel
 * @param {string} channelName - YouTube channel name
 * @returns {Promise<string>} Unique verified tag
 */
export const generateUniqueTag = async (channelName) => {
  try {
    // Clean channel name: remove special characters, spaces
    const cleanName = channelName.replace(/[^a-zA-Z0-9]/g, '');
    const baseTag = `@${cleanName}#YT`;
    
    // Check if tag exists
    const users = await searchUsersByTag(baseTag);
    
    // If tag doesn't exist, return it
    if (users.length === 0) {
      return baseTag;
    }
    
    // If tag exists, append a number
    let counter = 1;
    let uniqueTag = `@${cleanName}${counter}#YT`;
    
    while (true) {
      const existingUsers = await searchUsersByTag(uniqueTag);
      if (existingUsers.length === 0) {
        return uniqueTag;
      }
      counter++;
      uniqueTag = `@${cleanName}${counter}#YT`;
      
      // Safety limit
      if (counter > 1000) {
        throw new Error('Unable to generate unique tag');
      }
    }
  } catch (error) {
    console.error('Error generating unique tag:', error);
    throw error;
  }
};

/**
 * Update user settings
 * @param {string} uid - User ID
 * @param {Object} settings - Settings to update
 * @returns {Promise<void>}
 */
export const updateUserSettings = async (uid, settings) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...settings,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw new Error('Failed to update settings');
  }
};

// ==================== CHAT OPERATIONS ====================

/**
 * Create or get existing chat between two users
 * @param {string} uid1 - First user ID
 * @param {string} uid2 - Second user ID
 * @returns {Promise<string>} Chat ID
 */
export const createOrGetChat = async (uid1, uid2) => {
  try {
    // Sort UIDs to ensure consistent chat ID
    const members = [uid1, uid2].sort();
    const chatId = `${members[0]}_${members[1]}`;
    
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    // If chat doesn't exist, create it
    if (!chatSnap.exists()) {
      await setDoc(chatRef, {
        members,
        createdAt: serverTimestamp(),
        lastMessage: '',
        lastMessageTime: serverTimestamp(),
        unreadCount: {
          [uid1]: 0,
          [uid2]: 0,
        },
      });
    }
    
    return chatId;
  } catch (error) {
    console.error('Error creating/getting chat:', error);
    throw new Error('Failed to create chat');
  }
};

/**
 * Get all chats for a user
 * @param {string} uid - User ID
 * @returns {Promise<Array>} Array of chats
 */
export const getUserChats = async (uid) => {
  try {
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('members', 'array-contains', uid),
      orderBy('lastMessageTime', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error getting user chats:', error);
    throw new Error('Failed to fetch chats');
  }
};

/**
 * Subscribe to user's chats in real-time
 * @param {string} uid - User ID
 * @param {Function} callback - Callback function with chats array
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserChats = (uid, callback) => {
  try {
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('members', 'array-contains', uid),
      orderBy('lastMessageTime', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(chats);
    });
  } catch (error) {
    console.error('Error subscribing to chats:', error);
    throw new Error('Failed to subscribe to chats');
  }
};

// ==================== MESSAGE OPERATIONS ====================

/**
 * Send a message in a chat
 * @param {string} chatId - Chat ID
 * @param {string} senderId - Sender user ID
 * @param {string} type - Message type ('text' or 'audio')
 * @param {string} content - Message content (text or audio URL)
 * @returns {Promise<string>} Message ID
 */
export const sendMessage = async (chatId, senderId, type, content) => {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const messageDoc = await addDoc(messagesRef, {
      senderId,
      type,
      content,
      timestamp: serverTimestamp(),
      read: false,
    });
    
    // Update chat's last message
    const chatRef = doc(db, 'chats', chatId);
    await updateDoc(chatRef, {
      lastMessage: type === 'text' ? content : '🎤 Voice message',
      lastMessageTime: serverTimestamp(),
    });
    
    return messageDoc.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw new Error('Failed to send message');
  }
};

/**
 * Subscribe to messages in a chat
 * @param {string} chatId - Chat ID
 * @param {Function} callback - Callback function with messages array
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMessages = (chatId, callback) => {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(messages);
    });
  } catch (error) {
    console.error('Error subscribing to messages:', error);
    throw new Error('Failed to subscribe to messages');
  }
};

/**
 * Mark messages as read
 * @param {string} chatId - Chat ID
 * @param {Array<string>} messageIds - Array of message IDs to mark as read
 * @returns {Promise<void>}
 */
export const markMessagesAsRead = async (chatId, messageIds) => {
  try {
    const promises = messageIds.map(messageId => {
      const messageRef = doc(db, 'chats', chatId, 'messages', messageId);
      return updateDoc(messageRef, { read: true });
    });
    
    await Promise.all(promises);
  } catch (error) {
    console.error('Error marking messages as read:', error);
    // Don't throw - this is not critical
  }
};

// ==================== PERMISSION & PRIVACY OPERATIONS ====================

/**
 * Check if user can send messages to another user
 * @param {string} senderUid - Sender user ID
 * @param {string} recipientUid - Recipient user ID
 * @returns {Promise<boolean>} Whether sender can message recipient
 */
export const canSendMessage = async (senderUid, recipientUid) => {
  try {
    const recipient = await getUserById(recipientUid);
    
    if (!recipient) {
      return false;
    }
    
    const messagePermissions = recipient.messagePermissions || 'everyone';
    
    // If recipient allows everyone, return true
    if (messagePermissions === 'everyone') {
      return true;
    }
    
    // If recipient doesn't allow messages, return false
    if (messagePermissions === 'none') {
      return false;
    }
    
    // Check if sender is blocked
    if (recipient.blockedUsers && recipient.blockedUsers.includes(senderUid)) {
      return false;
    }
    
    // For 'subscribers' permission, would need YouTube API integration
    // For this implementation, we'll allow it as placeholder
    if (messagePermissions === 'subscribers') {
      // TODO: Implement YouTube subscriber check
      return true;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking message permissions:', error);
    return false;
  }
};

/**
 * Block a user
 * @param {string} uid - Current user ID
 * @param {string} blockedUid - User ID to block
 * @returns {Promise<void>}
 */
export const blockUser = async (uid, blockedUid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userData = await getUserById(uid);
    
    const blockedUsers = userData.blockedUsers || [];
    if (!blockedUsers.includes(blockedUid)) {
      blockedUsers.push(blockedUid);
    }
    
    await updateDoc(userRef, {
      blockedUsers,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error blocking user:', error);
    throw new Error('Failed to block user');
  }
};

/**
 * Report a user
 * @param {string} reporterUid - Reporter user ID
 * @param {string} reportedUid - Reported user ID
 * @param {string} reason - Reason for report
 * @returns {Promise<void>}
 */
export const reportUser = async (reporterUid, reportedUid, reason) => {
  try {
    const reportsRef = collection(db, 'reports');
    await addDoc(reportsRef, {
      reporterUid,
      reportedUid,
      reason,
      timestamp: serverTimestamp(),
      status: 'pending',
    });
  } catch (error) {
    console.error('Error reporting user:', error);
    throw new Error('Failed to report user');
  }
};
